/*!
 * bz-game-segue.js — the score→product segue for The Focus Challenge
 * Session 32D · projects/briefs/focus-game-2026-08-04/tactics.md §4 · HARDENING-AND-SEGUE-PLAN.md §B
 *
 * ┌─ WHAT THIS IS ───────────────────────────────────────────────────────────────┐
 * │ "Part 2". The game finishes, the score screen renders (owned by the caller),  │
 * │ and THIS module owns everything after it:                                     │
 * │                                                                               │
 * │   [Play again]  [See my pick]                                                 │
 * │        └─ See my pick → ONE preference question (multi-select, 2 options)     │
 * │                       → product card                                          │
 * │                       → [Buy once]  [Subscribe & Save]                        │
 * └───────────────────────────────────────────────────────────────────────────────┘
 *
 * ═════════════════════════ THE ROUTING RULE (HARD) ═════════════════════════
 * The product comes from the PREFERENCE ANSWER and from nothing else. The score,
 * the percentile, the per-round results and the paced time are NOT arguments to
 * anything in this file — `assertNoScoreInput()` throws at construction if a caller
 * passes any of them, so score→product causation is structurally impossible rather
 * than merely discouraged. Every string this module renders also goes through
 * `BZGame.assertNoConnective()` (guard() below), which throws on copy such as
 * "based on your result". Both guards are load-bearing: do not remove either to
 * make an edit compile.
 *
 * Routing map (Calum 2026-08-06, APPROVED — wording NOT to be re-worded):
 *   "What kind of day are you after?"  (pick either — or both)
 *     More push   "I want to get going and stay going"            → FOCUS PRO™
 *     Less noise  "I want things to settle so I can think straight" → FOCUS ELITE™
 *     both selected                                                → Starter Bundle
 *
 * FOCUS ORIGINAL™ IS NOT ROUTABLE from this question (Calum 2026-08-06: "original only for
 * children or sensitive disposition"). `assertRoutable()` throws if a future edit adds it as
 * a route target. It survives only as an optional passive SIGNPOST link (plan §C, Grok's milder
 * wording) — no self-identification question, no extra tap, no health question.
 *
 * 4-class lexicon: PRO = motivation / dopamine · ELITE = stress relief / GABA. Never cross-
 * compounded as a tier label. The Class-1 tier label is market-localised, so it lives in the
 * string set (segue.products.<key>.tier), never here.
 *
 * OFFER ARCHITECTURE (AGENTS.md — stack hero, demote code): Subscribe & Save is the HERO and is
 * rendered as the primary button. A single-order discount code is secondary and is NEVER
 * presented as stacking with the hero. `assertNoStackClaim()` is an EN-only best-effort backstop
 * on the offer notes — it is not a substitute for reading the DE/FR copy.
 *
 * i18n: this file contains ZERO user-visible English. Every string comes from the market string
 * set passed in as `opts.strings` (see REQUIRED_STRING_KEYS + assertStrings). Another agent
 * drafts the 4-market copy; `assertStrings()` fails closed on a missing key so a half-translated
 * market cannot render a blank button.
 *
 * Style: ES5, no framework, `var` only, no arrow functions, no optional chaining. IIFE on window.
 * Matches build/bz-game.js.
 */
(function (root) {
  'use strict';

  /* ══════════════════════════════════════════════════════════════════════════
   * CHECKOUT URL SHAPE — ONE-LINE SWAP
   *
   * VERDICT LANDED 2026-08-18 (ClickUp 869ekh98t, reported by Francis). Shape A `cart_permalink`
   * DOES NOT WORK: Shopify's cart permalink does not consume `selling_plan`. It forwards straight
   * to a checkout carrying the param as a dangling query string that nothing reads, so every
   * Subscribe & Save click billed a ONE-TIME order at the FULL price on all four live stores.
   * Verified live 12/12 (4 stores x 3 SKUs) against /cart.js `selling_plan_allocation`, with
   * shape B as the positive control proving the probe could observe a subscription line.
   * Shape B `cart_add_redirect` is 12/12 CORRECT, at prices matching `priceSub` exactly.
   *
   * KNOWN, TESTED behaviour change vs shape A: /cart/add ADDS to the existing cart where the
   * permalink REPLACED it. Mixed carts bill correctly (subscription line + one-time line each
   * keep their own allocation); a re-take stacks QUANTITY on the subscription line. Both are
   * visible at checkout before payment. Do NOT "fix" this by reintroducing a permalink.
   *
   * `discountCode` is NOT a /cart/add parameter — it must ride on `return_to`. bz-game-boot.js
   * does not currently pass one. If anyone wires `discountCode` at the boot site, re-verify:
   * silently dropping it is the SAME failure class as the bug this comment records.
   *
   * To adopt the verified shape: change THIS ONE LINE. Nothing else in the file knows the shape.
   * If the verifier finds a shape not listed, add it to URL_SHAPES and point this constant at it.
   * ══════════════════════════════════════════════════════════════════════════ */
  var CHECKOUT_URL_SHAPE = 'catalogue_links';

  var DEFAULT_QTY = 1;

  /* Transparent-cutout packshots for the product card (Calum 2026-08-07: embed product
   * pictures, "PNGs with invisible backgrounds"). Market-agnostic by design — the front labels
   * are English in every market (memory/feedback_english_only_front_labels.md), so one
   * canonical CDN file per SKU serves all four stores. Keyed by ROUTE key; a key with no entry
   * renders the text-only card rather than a broken image. */
  var PACKSHOTS = {
    pro: 'https://www.brainzyme.com/cdn/shop/files/Packshot_Focus_Single_Pack_Deals_Pro_2025.png',
    elite: 'https://www.brainzyme.com/cdn/shop/files/Packshot_Focus_Single_Pack_Deals_Elite_2025.png',
    bundle: 'https://www.brainzyme.com/cdn/shop/files/Packshot_Focus_StarterBundle_Website_HP_2025_f904ceec-4aac-4cad-a4fa-0d06ff3535b3.png'
  };

  /* ---------------------------------------------------------------- utils */
  function el(tag, cls, txt) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (txt != null) n.textContent = txt;
    return n;
  }
  function clear(n) { while (n.firstChild) n.removeChild(n.firstChild); }
  function assign(target, src) {
    if (!src) return target;
    for (var k in src) {
      if (Object.prototype.hasOwnProperty.call(src, k)) target[k] = src[k];
    }
    return target;
  }
  var _uid = 0;
  function uid(prefix) { _uid++; return 'bzseg-' + prefix + '-' + _uid; }

  /**
   * Same press-not-release binding as the engine. Falls back to plain click only if bz-game.js is
   * somehow absent, so the segue degrades rather than throwing.
   */
  function tap(node, fn) {
    if (root.BZGame && root.BZGame.onTap) return root.BZGame.onTap(node, fn);
    node.addEventListener('click', fn);
    return node;
  }

  /** Dotted lookup so a missing nested key returns undefined instead of throwing. */
  function pick(obj, path) {
    var parts = String(path).split('.');
    var cur = obj;
    for (var i = 0; i < parts.length; i++) {
      if (cur == null) return undefined;
      cur = cur[parts[i]];
    }
    return cur;
  }

  /* ══════════════════════════════════ URL BUILDING ══════════════════════════════════ */

  /**
   * Shopify ids arrive in several shapes depending on which API produced them: a bare numeric id
   * (Liquid / Storefront legacy) and a GraphQL global id ("gid://shopify/ProductVariant/123",
   * and older "…/SellingPlan:123" forms). A gid in a cart permalink 404s silently, which is the
   * kind of defect that only shows up on the live store.
   *
   * Take the trailing digit run — that is the id in every shape — and only fall back to the last
   * path segment when there is no numeric tail. A colon surviving into a cart permalink would
   * also collide with the `variant:qty` separator, so this closes two faults at once.
   */
  function normaliseId(id) {
    var s = String(id == null ? '' : id);
    var m = /(\d+)\s*$/.exec(s);
    if (m) return m[1];
    var cut = Math.max(s.lastIndexOf('/'), s.lastIndexOf(':'));
    return cut === -1 ? s : s.slice(cut + 1);
  }

  function trimBase(base) {
    var s = String(base == null ? '' : base);
    while (s.length && s.charAt(s.length - 1) === '/') s = s.slice(0, -1);
    return s;
  }

  /** utmParams + any extra query pairs → "a=b&c=d" (already encoded). '' when empty. */
  function queryFrom(pairs) {
    var out = [];
    for (var k in pairs) {
      if (!Object.prototype.hasOwnProperty.call(pairs, k)) continue;
      var v = pairs[k];
      if (v == null || v === '') continue;
      out.push(encodeURIComponent(k) + '=' + encodeURIComponent(v));
    }
    return out.join('&');
  }

  /**
   * The candidate shapes. Each takes the SAME normalised argument object and returns a full URL.
   * Keep every candidate here even after one is chosen — the losers are the evidence of what was
   * tried, and re-testing after a Shopify change is then a one-line swap rather than a rewrite.
   */
  var URL_SHAPES = {
    /**
     * A · Cart permalink (default guess).
     *   {base}/cart/{variant}:{qty}?selling_plan={id}&discount={code}&utm_*
     * Lands on the cart with the line already added. Documented, survives Shop Pay.
     */
    cart_permalink: function (o) {
      var q = {};
      if (o.sellingPlanId) q.selling_plan = o.sellingPlanId;
      if (o.discountCode) q.discount = o.discountCode;
      assign(q, o.utmParams);
      var qs = queryFrom(q);
      return o.storefrontBase + '/cart/' + o.variantId + ':' + o.qty + (qs ? '?' + qs : '');
    },

    /**
     * B · /cart/add with return_to.
     *   {base}/cart/add?id={variant}&quantity={qty}&selling_plan={id}&return_to=/checkout
     * Skips the cart page. `discount` is NOT a /cart/add parameter, so a code has to ride on the
     * return_to target — which is exactly the fragility the verifier is checking for.
     */
    cart_add_redirect: function (o) {
      var q = { id: o.variantId, quantity: o.qty };
      if (o.sellingPlanId) q.selling_plan = o.sellingPlanId;
      /* Line-item properties. `_`-prefixed = stored on the order, NOT rendered to the customer
         (verified 2026-08-18 on a real rendered checkout, not inferred from docs). This is the
         ONLY order-level join we have: without it a game-sourced order is indistinguishable from
         any other, which is exactly why the 869ekh98t mis-billing could not be scoped. Per-LINE,
         so it survives the additive cart a cart-level attribute could not disambiguate. */
      assign(q, o.lineProperties);
      assign(q, o.utmParams);
      var target = o.discountCode
        ? '/checkout?discount=' + encodeURIComponent(o.discountCode)
        : '/checkout';
      q.return_to = target;
      return o.storefrontBase + '/cart/add?' + queryFrom(q);
    },

    /**
     * C · Discount-wrapped permalink.
     *   {base}/discount/{code}?redirect=/cart/{variant}:{qty}?selling_plan={id}
     * The shape bz-game-reward.js already uses for the reward code, and the most reliable way to
     * make a code actually stick. Only meaningful when there IS a code; falls back to A when not.
     */
    /**
     * D · CATALOGUE FORMS — the canonical, estate-standard shapes (Calum 2026-08-18).
     *
     * SoT is the "Products Catalogue" Google Sheet (1NGDcT2qbO6mt4NJuPz5pzgDpqEXKvrFxAIYckd0p80k),
     * surfaced locally in config/brainzyme_product_matrix.json. These are the SAME forms used by
     * ~20 other theme surfaces (homepage bz-home-modular, bz-shelf-data, PageFly LP snippets,
     * adhd-supplements-guide.js) across all 4 markets. The game was the ONLY divergent surface.
     *
     *   subscription : {base}/a/subscriptions/checkout/{variant}:{qty}:{plan}?clear=false
     *   one-time     : {base}/cart/{variant}:{qty}
     *
     * `clear` is ALWAYS top-level; ONLY `discount` rides inside `attributes=` — canon, and the
     * subject of a documented flip-flop, so do NOT "correct" either form again without reading
     * memory/feedback_checkout_permalinks_catalog_sot.md first. The Sheet wins on conflict.
     *
     * `clear=false` (estate convention, 39/39 occurrences, zero `clear=true`) is ADDITIVE and that
     * is DELIBERATE: `clear=true` would DELETE items a customer already has in their basket.
     * "Stateless" here means one self-describing URL, not a cart wipe.
     *
     * VERIFIED LIVE 2026-08-18: sub -> GBP24.36 w/ "30-Day Subscription (16% OFF)"; one-time ->
     * GBP29.00 no plan; BOTH land directly on /checkouts/ (the one-time permalink does NOT stop
     * at the cart page).
     */
    catalogue_links: function (o) {
      if (o.sellingPlanId) {
        var subQ = o.discountCode
          ? 'attributes=discount%3D' + encodeURIComponent(o.discountCode) + '&clear=false'
          : 'clear=false';
        return o.storefrontBase + '/a/subscriptions/checkout/' +
               o.variantId + ':' + o.qty + ':' + o.sellingPlanId + '?' + subQ;
      }
      return o.storefrontBase + '/cart/' + o.variantId + ':' + o.qty +
             (o.discountCode ? '?discount=' + encodeURIComponent(o.discountCode) : '');
    },

    discount_wrapped: function (o) {
      if (!o.discountCode) return URL_SHAPES.cart_permalink(o);
      var q = {};
      if (o.sellingPlanId) q.selling_plan = o.sellingPlanId;
      assign(q, o.utmParams);
      var qs = queryFrom(q);
      var target = '/cart/' + o.variantId + ':' + o.qty + (qs ? '?' + qs : '');
      return o.storefrontBase + '/discount/' + encodeURIComponent(o.discountCode) +
             '?redirect=' + encodeURIComponent(target);
    }
  };

  /**
   * Build a checkout URL.
   *
   * @param {Object} opts
   *   storefrontBase {string}  REQUIRED — e.g. "https://www.brainzyme.com" (per-market; the four
   *                            stores are SEPARATE Shopify instances, so this is never shared)
   *   variantId      {string|number} REQUIRED — numeric or gid; normalised either way
   *   sellingPlanId  {string|number} optional — presence is what makes it a Subscribe & Save URL
   *   discountCode   {string}  optional
   *   utmParams      {Object}  optional — flat key/value, appended as-is
   *   lineProperties {Object}  optional — Shopify line-item properties, keys already in
   *                            `properties[_name]` form. Carried by cart_add_redirect ONLY; a
   *                            cart permalink cannot express them.
   *   qty            {number}  optional — defaults to 1
   *   shape          {string}  optional — override CHECKOUT_URL_SHAPE for one call (testing only)
   * @returns {string} absolute URL
   */
  function buildCheckoutUrl(opts) {
    var o = opts || {};
    if (!o.storefrontBase) throw new Error('bz-game-segue: storefrontBase required');
    if (o.variantId == null || o.variantId === '') throw new Error('bz-game-segue: variantId required');

    var shapeKey = o.shape || CHECKOUT_URL_SHAPE;
    var shape = URL_SHAPES[shapeKey];
    if (!shape) throw new Error('bz-game-segue: unknown checkout URL shape "' + shapeKey + '"');

    return shape({
      storefrontBase: trimBase(o.storefrontBase),
      variantId: normaliseId(o.variantId),
      sellingPlanId: o.sellingPlanId ? normaliseId(o.sellingPlanId) : null,
      discountCode: o.discountCode || null,
      utmParams: o.utmParams || null,
      lineProperties: o.lineProperties || null,
      qty: o.qty || DEFAULT_QTY
    });
  }

  /**
   * Order-level provenance markers for a segue checkout.
   *
   * WHY THIS EXISTS: on 2026-08-18 a checkout defect (ClickUp 869ekh98t) billed every Subscribe &
   * Save click as a full-price one-time order. The affected customers could not be identified —
   * the checkout link carried no UTM, no cart attribute and no line property, so a broken-S&S
   * order was indistinguishable from a genuine buy-once order in Shopify. `landing_site` matched
   * 4 of 665 orders and is a FLOOR, not a count. These three markers are the fix for that blind
   * spot: they ride into the ORDER, not just into analytics.
   *
   * @param {string} routeKey  'pro' | 'elite' | 'bundle'
   * @param {string} offer     'sub' (Subscribe & Save) | 'once' (one-time)
   */
  /**
   * RETIRED 2026-08-18 — kept ONLY as the record of what was tried, never called.
   *
   * These line-item properties DID work on the `cart_add_redirect` shape (verified: they landed on
   * the cart line, customer-invisible, selling plan intact). They are DEAD on the catalogue Seal
   * proxy: `properties[...]` AND `attributes=<anything but discount>` are both SILENTLY DROPPED
   * (`props={}`, `attributes={}` — tested live twice). Re-adding them here would ship code that
   * looks like instrumentation and measures nothing.
   *
   * CONSEQUENCE, stated plainly: on the catalogue link form there is NO order-level provenance.
   * A game-sourced order is not distinguishable from any other in Shopify order data. That is the
   * blind spot that made ClickUp 869ekh98t unscopeable. Restoring it needs a Shopify Flow order
   * tag or an equivalent server-side join — NOT a URL parameter. Open: spine T-435b6c.
   */
  function retiredLineMarkers_doNotUse(routeKey, offer) {   // eslint-disable-line no-unused-vars
    return {
      'properties[_bz_src]': 'focus-challenge',
      'properties[_bz_route]': routeKey,
      'properties[_bz_offer]': offer
    };
  }

  /**
   * ORDER-LEVEL PROVENANCE  (the ONLY thing that makes the Shopify Flow order-tag possible)
   *
   * WHY THIS EXISTS
   * ---------------
   * On 2026-08-18 a checkout defect (ClickUp 869ekh98t) billed every "Subscribe & Save" click on
   * The Focus Challenge as a full-price ONE-TIME order, on all four stores. It could not be scoped
   * afterwards, because NOTHING on the order said the customer came from the game. That blind spot,
   * not the broken link, is what made the incident expensive.
   *
   * WHY IT IS DONE THIS WAY (do not "simplify" this away)
   * ----------------------------------------------------
   * The checkout URL itself is the CANONICAL catalogue link and must stay that way. But the Seal
   * app proxy behind `/a/subscriptions/checkout/...` SILENTLY DROPS every marker you try to put in
   * the URL - both `properties[...]` and `attributes=<anything except discount>` were tested live
   * and came back empty. Cart ATTRIBUTES set BEFORE navigation, however, DO survive it (tested).
   * So the provenance is stamped on the cart first, then the canonical URL is called unchanged.
   * The checkout call stays stateless and canonical; only the stamp is stateful.
   *
   * `_`-prefixed so the customer never sees it. Verified on a real rendered checkout at 390x844,
   * not inferred from documentation.
   *
   * FAIL-OPEN BY DESIGN: this must NEVER stop someone reaching checkout. The fetch is raced against
   * a short timer, and navigation happens regardless of whether the stamp succeeded. Losing a
   * provenance tag is a reporting gap; blocking checkout is lost revenue. If you change one thing
   * in this function, keep that ordering.
   *
   * CONSUMED BY: Shopify Flow "Tag Focus Challenge orders" on each of the 4 stores - it reads
   * order note attribute `_bz_src` and applies the order tag. See
   * reference/it-sops/focus-challenge-order-tagging.md. Spine T-435b6c.
   *
   * KNOWN LIMIT: cart attributes are CART-level, not per-line. With `clear=false` (the estate
   * convention, deliberately additive so a customer's existing basket is never deleted) a mixed
   * cart carries one stamp for the whole order. So the tag means "this order involved the game",
   * NOT "this line came from the game". Do not build per-line revenue attribution on it.
   */
  var STAMP_TIMEOUT_MS = 700;

  function stampProvenanceThenGo(ev, href, routeKey, offer) {
    if (!href || !root.fetch || !ev || typeof ev.preventDefault !== 'function') return;
    ev.preventDefault();
    var went = false;
    function go() { if (!went) { went = true; root.location.href = href; } }
    var timer = root.setTimeout(go, STAMP_TIMEOUT_MS);
    try {
      root.fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: {
          _bz_src: 'focus-challenge',
          _bz_route: routeKey || '',
          _bz_offer: offer || ''
        } })
      })['catch'](function () { /* stamp is best-effort */ })
       .then(function () { root.clearTimeout(timer); go(); }, function () { root.clearTimeout(timer); go(); });
    } catch (e) { root.clearTimeout(timer); go(); }
  }

  /* ══════════════════════════════════ COMPLIANCE GUARDS ══════════════════════════════════ */

  /**
   * Every string this module puts on screen goes through here. Fail-CLOSED: if BZGame is not
   * loaded, the guard is absent, and a compliance surface that cannot check itself must not
   * render at all. (A silent no-op guard is worse than no guard — it reads as protection.)
   */
  function guard(text) {
    if (!root.BZGame || typeof root.BZGame.assertNoConnective !== 'function') {
      throw new Error('bz-game-segue: BZGame.assertNoConnective unavailable — load bz-game.js first');
    }
    root.BZGame.assertNoConnective(text);
    return text;
  }

  /** guard() + set text in one step, so there is no path that renders an unchecked string. */
  function say(node, text) {
    node.textContent = guard(text == null ? '' : String(text));
    return node;
  }

  /**
   * The strongest available form of "the score does not drive the product": the score is not in
   * scope. Anything score-shaped in the options bag is a construction-time error.
   */
  var SCORE_SHAPED_KEYS = [
    'score', 'compositeScore', 'results', 'rounds', 'percentile', 'speedPercentile',
    'pacedMs', 'totalMs', 'population', 'personalBest', 'accuracy', 'rtMs'
  ];
  function assertNoScoreInput(opts) {
    if (!opts) return true;
    for (var i = 0; i < SCORE_SHAPED_KEYS.length; i++) {
      var k = SCORE_SHAPED_KEYS[i];
      if (Object.prototype.hasOwnProperty.call(opts, k)) {
        throw new Error('bz-game-segue: "' + k + '" passed to the segue. Routing comes ONLY from ' +
                        'the preference answer — the score must not be in scope here.');
      }
    }
    return true;
  }

  /** FOCUS ORIGINAL™ is deliberately not reachable from this question (Calum 2026-08-06). */
  var ROUTABLE_KEYS = ['pro', 'elite', 'bundle'];
  var NEVER_ROUTABLE = ['original'];
  function assertRoutable(key) {
    for (var i = 0; i < NEVER_ROUTABLE.length; i++) {
      if (key === NEVER_ROUTABLE[i]) {
        throw new Error('bz-game-segue: "' + key + '" is not routable from the preference question ' +
                        '(Calum 2026-08-06 — ORIGINAL is for children or a sensitive disposition, ' +
                        'and that is not something this question asks).');
      }
    }
    if (ROUTABLE_KEYS.indexOf(key) === -1) {
      throw new Error('bz-game-segue: unknown route target "' + key + '"');
    }
    return true;
  }

  /**
   * EN-only best-effort backstop for the offer-architecture rule (AGENTS.md: stack hero, demote
   * code). A single-order code must never be PRESENTED as stacking with Subscribe & Save. This
   * catches the obvious English phrasings; DE/FR still need a human read.
   */
  var STACK_CLAIM = ['stacks with', 'stack with', 'on top of your subscription',
                     'combine with your subscription', 'combines with subscribe'];
  function assertNoStackClaim(text) {
    var low = String(text || '').toLowerCase();
    for (var i = 0; i < STACK_CLAIM.length; i++) {
      if (low.indexOf(STACK_CLAIM[i]) !== -1) {
        throw new Error('bz-game-segue: offer copy presents a single-order code as stacking with ' +
                        'the Subscribe & Save hero: "' + STACK_CLAIM[i] + '"');
      }
    }
    return true;
  }

  /* ══════════════════════════════════ STRING CONTRACT ══════════════════════════════════ */

  /**
   * Every key this module reads, as a dotted path into the market string set. Another agent
   * writes the copy for uk/us/de/fr — this list is the contract they are writing against.
   * `ui.playAgain` already exists in bz-game-i18n.js; everything under `segue.` is new.
   */
  var REQUIRED_STRING_KEYS = [
    'ui.playAgain',              // EXISTS — reused, not re-authored
    'segue.seeMyPick',           // 2nd button on the score screen. Neutral — never a product name.
    'segue.question',            // "What kind of day are you after?" — APPROVED, do not reword
    'segue.questionHint',        // "(pick either — or both)"
    'segue.pushLabel',           // "More push"
    'segue.pushBody',            // "I want to get going and stay going"
    'segue.calmLabel',           // "Less noise"
    'segue.calmBody',            // "I want things to settle so I can think straight"
    'segue.pickPrompt',          // shown while nothing is selected; also the continue btn's description
    'segue.continueCta',         // proceeds to the product card
    'segue.changeAnswer',        // back to the question from the product card
    'segue.pickHeading',         // heading above the product card
    'segue.buyOnce',             // secondary CTA label
    'segue.subscribe',           // HERO CTA label (Subscribe & Save)
    'segue.subscribeNote',       // hero value line. No % figure unless it is the verified one.
    'segue.buyOnceNote',         // secondary note. Must not claim the code stacks with the hero.
    'segue.products.pro.tier',   // Class-1 tier label, localised (motivation / Motivation / motivation)
    'segue.products.pro.line',   // one-line product description
    'segue.products.elite.tier', // (stress relief / Stressabbau / anti-stress)
    'segue.products.elite.line',
    'segue.products.bundle.tier',
    'segue.products.bundle.line'
  ];

  /** Optional keys — absent means the feature is simply not rendered for that market. */
  var OPTIONAL_STRING_KEYS = [
    'segue.gentlerSignpost'      // "Prefer a gentler start? FOCUS ORIGINAL™" — a PASSIVE signpost,
                                 // never a route target (plan §C). Needs segue.gentlerHref too.
  ];

  /**
   * Fail closed on a half-translated market. A missing key would otherwise render an empty
   * button, which is a silent failure on the highest-intent screen in the whole flow.
   */
  function assertStrings(strings) {
    var missing = [];
    for (var i = 0; i < REQUIRED_STRING_KEYS.length; i++) {
      var v = pick(strings, REQUIRED_STRING_KEYS[i]);
      if (typeof v !== 'string' || !v.length) missing.push(REQUIRED_STRING_KEYS[i]);
    }
    if (missing.length) {
      throw new Error('bz-game-segue: missing string keys — ' + missing.join(', '));
    }
    return true;
  }

  /* ══════════════════════════════════ ROUTING ══════════════════════════════════ */

  /**
   * The ENTIRE routing decision. Two booleans in, one product key out. Nothing else is consulted.
   * Kept as a pure function so it is trivially testable and impossible to accidentally widen.
   */
  function resolveRoute(sel) {
    var push = !!(sel && sel.push);
    var calm = !!(sel && sel.calm);
    if (push && calm) return 'bundle';
    if (push) return 'pro';
    if (calm) return 'elite';
    return null;                      // nothing selected — the caller must not advance
  }

  /* ══════════════════════════════════ THE SEGUE ══════════════════════════════════ */

  /**
   * @param {Object} opts
   *   strings   {Object} REQUIRED — the market string set (bz-game-i18n.js `get(market)`)
   *   products  {Object} REQUIRED — { pro:{}, elite:{}, bundle:{} }, each:
   *                        { name, variantId, sellingPlanId, priceOnce, priceSub }
   *                      `name` is the trademarked SKU name (identical in all markets — the front
   *                      labels are English by design); `priceOnce`/`priceSub` are pre-formatted
   *                      per-market price strings, because currency is market DATA, not copy.
   *   storefrontBase {string} REQUIRED — this market's store origin
   *   discountCode   {string} optional — a single-order code; demoted, never shown as stacking
   *   utmParams      {Object} optional
   *   onPlayAgain    {Function} REQUIRED — restarts the game
   *   onRoute        {Function} optional — (productKey, selection) analytics hook. Receives the
   *                      PREFERENCE only; there is deliberately no score to pass it.
   *   gentlerHref    {string} optional — enables the ORIGINAL signpost when the string exists too
   */
  function BZSegue(opts) {
    assertNoScoreInput(opts);
    this.opts = assign({}, opts || {});
    if (!this.opts.strings) throw new Error('bz-game-segue: strings required');
    if (!this.opts.products) throw new Error('bz-game-segue: products required');
    if (!this.opts.storefrontBase) throw new Error('bz-game-segue: storefrontBase required');
    if (typeof this.opts.onPlayAgain !== 'function') {
      throw new Error('bz-game-segue: onPlayAgain required');
    }
    assertStrings(this.opts.strings);
    this.s = this.opts.strings;
    this.sel = { push: false, calm: false };
  }

  /**
   * Mount into a container that sits BELOW the already-rendered score.
   * Order is science → score → preference → product (tactics.md §14.4, Grok's IA rule): the
   * product must never be adjacent enough to the phenomenon to read as its remedy.
   */
  BZSegue.prototype.mount = function (container) {
    this.container = container;
    this.renderFork();
    return this;
  };

  /* ------------------------------------------------------------ step 1 · fork */
  BZSegue.prototype.renderFork = function () {
    var self = this;
    clear(this.container);
    var row = el('div', 'bzg-seg-fork');

    var again = el('button', 'bzg-btn bzg-seg-btn--ghost');
    again.type = 'button';
    say(again, pick(this.s, 'ui.playAgain'));
    tap(again, function () { self.opts.onPlayAgain(); });

    var pickBtn = el('button', 'bzg-btn bzg-seg-btn--primary');
    pickBtn.type = 'button';
    say(pickBtn, pick(this.s, 'segue.seeMyPick'));
    tap(pickBtn, function () { self.renderQuestion(); });

    row.appendChild(again);
    row.appendChild(pickBtn);
    this.container.appendChild(row);
  };

  /* -------------------------------------------------- step 2 · the ONE question */
  BZSegue.prototype.renderQuestion = function () {
    var self = this;
    clear(this.container);

    var wrap = el('div', 'bzg-seg');
    var qId = uid('q');
    var hintId = uid('hint');

    var h = el('h3', 'bzg-seg__q');
    h.id = qId;
    h.setAttribute('tabindex', '-1');       // focus target on step change, not a tab stop
    say(h, pick(this.s, 'segue.question'));
    wrap.appendChild(h);

    var hint = el('p', 'bzg-seg__hint');
    hint.id = hintId;
    say(hint, pick(this.s, 'segue.questionHint'));
    wrap.appendChild(hint);

    // A group, so a screen reader hears the two toggles as one question rather than two
    // unrelated buttons. They are toggles (aria-pressed), NOT radios — both can be on.
    var group = el('div', 'bzg-seg__opts');
    group.setAttribute('role', 'group');
    group.setAttribute('aria-labelledby', qId + ' ' + hintId);

    var pushBtn = this.optionButton('push', 'segue.pushLabel', 'segue.pushBody');
    var calmBtn = this.optionButton('calm', 'segue.calmLabel', 'segue.calmBody');
    group.appendChild(pushBtn);
    group.appendChild(calmBtn);
    wrap.appendChild(group);

    // Live region: announces "nothing selected yet" → cleared once something is on. Same node
    // is the continue button's description, so the reason it is disabled is discoverable.
    var status = el('p', 'bzg-seg__status');
    status.id = uid('status');
    status.setAttribute('aria-live', 'polite');
    say(status, pick(this.s, 'segue.pickPrompt'));
    wrap.appendChild(status);

    var go = el('button', 'bzg-btn bzg-seg-btn--primary');
    go.type = 'button';
    go.disabled = true;
    go.setAttribute('aria-describedby', status.id);
    say(go, pick(this.s, 'segue.continueCta'));
    tap(go, function () {
      var key = resolveRoute(self.sel);
      if (!key) return;                     // belt and braces — button is disabled anyway
      self.renderProduct(key);
    });
    wrap.appendChild(go);

    this.container.appendChild(wrap);

    this._syncSelection = function () {
      var key = resolveRoute(self.sel);
      go.disabled = !key;
      status.textContent = key ? '' : guard(pick(self.s, 'segue.pickPrompt'));
    };

    h.focus();
  };

  /** One multi-select toggle. Real <button> → keyboard + Enter/Space for free. */
  BZSegue.prototype.optionButton = function (key, labelKey, bodyKey) {
    var self = this;
    var b = el('button', 'bzg-seg-opt');
    b.type = 'button';
    // Initialise FROM this.sel, never hardcoded false. renderQuestion() rebuilds these buttons on
    // every entry — including "Change my answer" — but this.sel persists. Hardcoding false made the
    // rendered state disagree with the routing state, so a visitor who picked More push, continued,
    // went back and picked Less noise was routed to the BUNDLE instead of ELITE, with both buttons
    // looking like a single fresh choice. Deriving the view from the state fixes the mismatch AND
    // keeps their previous answer visible, which is what "change my answer" implies.
    b.setAttribute('aria-pressed', String(!!this.sel[key]));
    if (this.sel[key]) b.classList.add('is-on');
    b.style.touchAction = 'manipulation';

    var lab = el('span', 'bzg-seg-opt__label');
    say(lab, pick(this.s, labelKey));
    var body = el('span', 'bzg-seg-opt__body');
    say(body, pick(this.s, bodyKey));
    b.appendChild(lab);
    b.appendChild(body);

    // click, not pointerdown: nothing here is timed, and click is what keyboard activation fires.
    tap(b, function () {
      self.sel[key] = !self.sel[key];
      b.setAttribute('aria-pressed', String(self.sel[key]));
      if (self.sel[key]) b.classList.add('is-on');
      else b.classList.remove('is-on');
      if (self._syncSelection) self._syncSelection();
    });
    return b;
  };

  /* ------------------------------------------------- step 3 · product + checkout */
  BZSegue.prototype.renderProduct = function (key) {
    var self = this;
    assertRoutable(key);
    var product = this.opts.products[key];
    if (!product) throw new Error('bz-game-segue: no product configured for "' + key + '"');
    if (!product.variantId) throw new Error('bz-game-segue: no variantId for "' + key + '"');

    if (typeof this.opts.onRoute === 'function') {
      // Preference only. There is no score in this module to leak into analytics.
      this.opts.onRoute(key, { push: this.sel.push, calm: this.sel.calm });
    }

    clear(this.container);
    var wrap = el('div', 'bzg-seg bzg-seg--product');

    var h = el('h3', 'bzg-seg__q');
    h.setAttribute('tabindex', '-1');
    say(h, pick(this.s, 'segue.pickHeading'));
    wrap.appendChild(h);

    var card = el('div', 'bzg-seg-card');
    if (PACKSHOTS[key]) {
      var shot = el('img', 'bzg-seg-card__img');
      shot.src = PACKSHOTS[key];
      shot.alt = product.name;
      shot.decoding = 'async';
      // a CDN miss degrades to the old text-only card, never a broken-image icon
      shot.onerror = function () { if (shot.parentNode) { shot.parentNode.removeChild(shot); } };
      card.appendChild(shot);
    }
    // The SKU name is a trademark and identical in every market (front labels are English by
    // design) — it comes from the product config, not the string set.
    var name = el('p', 'bzg-seg-card__name');
    say(name, product.name);
    card.appendChild(name);

    // Class-1 tier label, localised. Never cross-compounded with the other SKU's vocabulary.
    var tier = el('p', 'bzg-seg-card__tier');
    say(tier, pick(this.s, 'segue.products.' + key + '.tier'));
    card.appendChild(tier);

    var line = el('p', 'bzg-seg-card__line');
    say(line, pick(this.s, 'segue.products.' + key + '.line'));
    card.appendChild(line);
    wrap.appendChild(card);

    /* ---- the two checkout buttons. S&S is the HERO and renders FIRST. ---- */
    var subNote = pick(this.s, 'segue.subscribeNote');
    var onceNote = pick(this.s, 'segue.buyOnceNote');
    assertNoStackClaim(subNote);
    assertNoStackClaim(onceNote);

    var subUrl = buildCheckoutUrl({
      storefrontBase: this.opts.storefrontBase,
      variantId: product.variantId,
      sellingPlanId: product.sellingPlanId,   // presence is what makes this the S&S URL
      discountCode: this.opts.discountCode,
      utmParams: this.opts.utmParams
    });
    var onceUrl = buildCheckoutUrl({
      storefrontBase: this.opts.storefrontBase,
      variantId: product.variantId,
      sellingPlanId: null,
      discountCode: this.opts.discountCode,
      utmParams: this.opts.utmParams
    });

    var subBtn = this.checkoutButton('bzg-seg-btn--primary', 'segue.subscribe',
                                     product.priceSub, subUrl, key, 'sub');
    wrap.appendChild(subBtn);
    if (subNote) { wrap.appendChild(say(el('p', 'bzg-seg__note bzg-seg__note--hero'), subNote)); }

    var onceBtn = this.checkoutButton('bzg-seg-btn--ghost', 'segue.buyOnce',
                                      product.priceOnce, onceUrl, key, 'once');
    wrap.appendChild(onceBtn);
    if (onceNote) { wrap.appendChild(say(el('p', 'bzg-seg__note'), onceNote)); }

    /* ---- passive ORIGINAL signpost. A LINK, never a route target (plan §C). ---- */
    var signpost = pick(this.s, 'segue.gentlerSignpost');
    if (signpost && this.opts.gentlerHref) {
      var a = el('a', 'bzg-seg__signpost');
      a.href = this.opts.gentlerHref;
      say(a, signpost);
      wrap.appendChild(a);
    }

    var back = el('button', 'bzg-btn bzg-seg-btn--link');
    back.type = 'button';
    say(back, pick(this.s, 'segue.changeAnswer'));
    tap(back, function () { self.renderQuestion(); });
    wrap.appendChild(back);

    this.container.appendChild(wrap);
    h.focus();
  };

  /**
   * A checkout CTA. A real <button> (per the accessibility brief) that navigates, rather than an
   * <a> styled as a button — so it is a single consistent control class across the whole segue.
   */
  /**
   * The two checkout CTAs.
   *
   * These are <a href> and NOT <button>. They navigate — they do not submit — and browser E2E on
   * 2026-08-07 showed the button form costs real things on the highest-intent screen in the funnel:
   * no middle-click, no open-in-new-tab, no copy-link, no status-bar preview of where you are about
   * to go. It also made the destination unverifiable, because the URL only existed inside a click
   * handler — no gate, and no reviewer, could ever check which variant a button was going to buy.
   * With an href the destination is inspectable by the E2E, which is how the per-market variant
   * assertion works at all.
   */
  BZSegue.prototype.checkoutButton = function (cls, labelKey, price, url, routeKey, offer) {
    var a = el('a', 'bzg-btn ' + cls);
    a.setAttribute('href', url);
    /* Click instrumentation. Before 2026-08-18 this button emitted NOTHING — no dataLayer push,
       no beacon — so the game->checkout step was completely unmeasured and, when the S&S link
       broke, there was no record that anyone had ever pressed it. This does NOT give an
       order-level join (the catalogue Seal proxy drops every URL-borne marker); it gives the
       FUNNEL step. Fire-and-forget, never blocks navigation. */
    a.setAttribute('data-bz-route', routeKey || '');
    a.setAttribute('data-bz-offer', offer || '');
    a.addEventListener('click', function (ev) {
      try {
        root.dataLayer = root.dataLayer || [];
        root.dataLayer.push({
          event: 'bz_game_checkout_click',
          bz_src: 'focus-challenge',
          bz_route: routeKey || '',
          bz_offer: offer || '',
          bz_shape: CHECKOUT_URL_SHAPE
        });
      } catch (e) { /* never block the customer reaching checkout */ }
      stampProvenanceThenGo(ev, a.getAttribute('href'), routeKey, offer);
    });
    a.setAttribute('role', 'button');       // it looks and behaves like one to AT
    var label = el('span', 'bzg-seg-cta__label');
    say(label, pick(this.s, labelKey));
    a.appendChild(label);
    if (price) {
      var p = el('span', 'bzg-seg-cta__price');
      say(p, price);                       // pre-formatted per market; currency is data, not copy
      a.appendChild(p);
    }
    return a;
  };

  /* ══════════════════════════════════ STYLES ══════════════════════════════════
   * Injected once, opt-in via injectStyles(). Uses the EXISTING custom properties with literal
   * fallbacks so it is correct inside the harness and inside the theme.
   *
   * CONTRAST (WCAG 2.x, checked): #fff on --bz-blue-dark #0A5FB8 = 5.7:1 · --bz-ink #1d1f21 on
   * #fff = 15.9:1 · --bz-grey #6d6e71 on #fff = 5.3:1. --bz-yellow #FFC42E is used ONLY as a
   * non-text accent — it fails as text on white and must never be used for one.
   * Focus is a 3px --bz-blue-dark ring with a 2px offset (non-text contrast 5.7:1 vs white).
   */
  var STYLE_ID = 'bzg-segue-styles';
  var CSS = [
    '.bzg-seg{text-align:left}',
    '.bzg-seg-fork{display:grid;grid-template-columns:1fr 1fr;gap:9px}',
    '.bzg-seg__q{margin:0 0 4px;font-size:19px;line-height:1.3;font-weight:800;',
      'color:var(--bz-ink,#1d1f21)}',
    '.bzg-seg__q:focus-visible{outline:3px solid var(--bz-blue-dark,#0A5FB8);outline-offset:2px}',
    '.bzg-seg__hint{margin:0 0 14px;font-size:14px;color:var(--bz-grey,#6d6e71)}',
    '.bzg-seg__opts{display:grid;gap:9px;margin-bottom:12px}',
    '.bzg-seg-opt{display:block;width:100%;text-align:left;padding:14px 15px;cursor:pointer;',
      'background:#fff;border:2px solid #cfd6dd;border-radius:12px;font:inherit;',
      'color:var(--bz-ink,#1d1f21);touch-action:manipulation}',
    '.bzg-seg-opt__label{display:block;font-weight:800;font-size:16px;margin-bottom:3px}',
    '.bzg-seg-opt__body{display:block;font-size:14px;line-height:1.45;',
      'color:var(--bz-grey,#6d6e71)}',
    '.bzg-seg-opt[aria-pressed="true"]{border-color:var(--bz-blue-dark,#0A5FB8);',
      'background:#eef5fd}',
    // selection is never signalled by colour alone (WCAG 1.4.1) — the tick is the second channel
    '.bzg-seg-opt[aria-pressed="true"] .bzg-seg-opt__label::after{content:" \\2713";',
      'color:var(--bz-blue-dark,#0A5FB8)}',
    '.bzg-seg-opt:focus-visible,.bzg-btn:focus-visible{outline:3px solid ',
      'var(--bz-blue-dark,#0A5FB8);outline-offset:2px}',
    '.bzg-seg__status{margin:0 0 12px;font-size:14px;color:var(--bz-grey,#6d6e71);min-height:1.2em}',
    '.bzg-seg-card{border:1px solid #e3e8ee;border-radius:12px;padding:18px 15px 15px;',
      'margin-bottom:16px;text-align:center}',
    // transparent-cutout packshot; sized by HEIGHT so portrait pots and the landscape bundle
    // render at the same visual weight
    '.bzg-seg-card__img{display:block;margin:0 auto 12px;height:150px;width:auto;max-width:100%}',
    '.bzg-seg-card__name{margin:0 0 2px;font-size:18px;font-weight:800;',
      'color:var(--bz-ink,#1d1f21)}',
    '.bzg-seg-card__tier{margin:0 0 8px;font-size:12px;font-weight:700;letter-spacing:.09em;',
      'text-transform:uppercase;color:var(--bz-blue-dark,#0A5FB8)}',
    '.bzg-seg-card__line{margin:0;font-size:15px;line-height:1.5;color:var(--bz-ink,#1d1f21)}',
    '.bzg-seg-btn--primary{background:var(--bz-blue-dark,#0A5FB8);color:#fff;border:2px solid ',
      'var(--bz-blue-dark,#0A5FB8)}',
    '.bzg-seg-btn--ghost{background:#fff;color:var(--bz-blue-dark,#0A5FB8);',
      'border:2px solid var(--bz-blue-dark,#0A5FB8)}',
    // id-scoped + !important: the Be Yours theme restyles anchors inside its containers as
    // FILLED buttons, which turned 'change my answer' into a third primary-looking CTA sitting
    // next to the two purchase buttons. Three competing blue blocks is bad hierarchy on the
    // highest-intent screen. Only visible on the live theme — the local preview has no cascade.
    '#bz-game-mount .bzg-seg-btn--link,.bzg-seg-btn--link{background:transparent!important;',
      'color:var(--bz-grey,#6d6e71)!important;border:0!important;box-shadow:none!important;',
      'text-decoration:underline;font-weight:600}',
    '.bzg-btn[role=button]{display:block;text-align:center;text-decoration:none;box-sizing:border-box}',
    '.bzg-seg-cta__label{display:block;font-size:16px;font-weight:700;line-height:1.2}',
    '.bzg-seg-cta__price{margin-left:8px;font-weight:600;opacity:.92}',
    '.bzg-seg__note{margin:6px 0 14px;font-size:13px;line-height:1.45;',
      'color:var(--bz-grey,#6d6e71)}',
    '.bzg-seg__note--hero{color:var(--bz-ink,#1d1f21);font-weight:600}',
    '.bzg-seg__signpost{display:inline-block;margin:2px 0 14px;font-size:14px;',
      'color:var(--bz-blue-dark,#0A5FB8)}',
    '@media (prefers-reduced-motion: reduce){.bzg-seg-opt{transition:none}}'
  ].join('');

  function injectStyles(doc) {
    var d = doc || document;
    if (d.getElementById(STYLE_ID)) return false;
    var st = d.createElement('style');
    st.id = STYLE_ID;
    st.appendChild(d.createTextNode(CSS));
    (d.head || d.documentElement).appendChild(st);
    return true;
  }

  /* ------------------------------------------------------------------ exports */
  root.BZSegue = BZSegue;
  root.BZSegue.buildCheckoutUrl = buildCheckoutUrl;
  root.BZSegue.CHECKOUT_URL_SHAPE = CHECKOUT_URL_SHAPE;
  root.BZSegue.URL_SHAPES = URL_SHAPES;
  root.BZSegue.resolveRoute = resolveRoute;
  root.BZSegue.ROUTABLE_KEYS = ROUTABLE_KEYS;
  root.BZSegue.NEVER_ROUTABLE = NEVER_ROUTABLE;
  root.BZSegue.REQUIRED_STRING_KEYS = REQUIRED_STRING_KEYS;
  root.BZSegue.OPTIONAL_STRING_KEYS = OPTIONAL_STRING_KEYS;
  root.BZSegue.assertStrings = assertStrings;
  root.BZSegue.assertRoutable = assertRoutable;
  root.BZSegue.assertNoScoreInput = assertNoScoreInput;
  root.BZSegue.assertNoStackClaim = assertNoStackClaim;
  root.BZSegue.injectStyles = injectStyles;
  root.BZSegue.CSS = CSS;
})(window);
